/**
 * The page side of the job protocol. One worker, started on the first job,
 * serves jobs in order. When a host gives no worker, or its worker cannot start,
 * the runner runs the same jobs in-thread, so the bytes never depend on which
 * path ran them (ADR-0042).
 */
import { warn } from '../logger.js';
import { fromJobError } from './protocol';
/**
 * Written only by `inThreadJobs`, on its first call, and never cleared. A dynamic import, as the
 * jobs bundle `fastnoise-lite`, which a host with a worker never runs on the page.
 */
let inThreadJobsLoad = null;
function inThreadJobs() {
    inThreadJobsLoad ??= import('./jobs').then((m) => m.WORKER_JOBS);
    return inThreadJobsLoad;
}
function abortError() {
    return new DOMException('The worker job was aborted', 'AbortError');
}
export class WorkerJobRunner {
    createWorker;
    /** Written by `acquireWorker`. Cleared by an abort, a worker error and `dispose`. */
    worker = null;
    liveWorker = null;
    /** Set once, for good, by the first failure to start a worker. */
    inThread;
    queue = [];
    running = null;
    nextId = 1;
    constructor(options = {}) {
        this.createWorker = options.createWorker;
        this.inThread = !options.createWorker;
    }
    run(name, input, signal) {
        return new Promise((resolve, reject) => {
            if (signal?.aborted) {
                reject(abortError());
                return;
            }
            const job = {
                id: this.nextId++,
                name,
                input,
                signal,
                resolve: resolve,
                reject,
                onAbort: () => this.abort(job),
                settled: false,
            };
            signal?.addEventListener('abort', job.onAbort, { once: true });
            this.queue.push(job);
            this.pump();
        });
    }
    /** Aborts every job and stops the worker. The runner starts a new one for a later job. */
    dispose() {
        for (const job of [...this.queue])
            this.abort(job);
        if (this.running)
            this.abort(this.running);
        this.stopWorker();
    }
    pump() {
        if (this.running)
            return;
        const job = this.queue.shift();
        if (!job)
            return;
        this.running = job;
        if (this.inThread)
            this.runInThread(job);
        else
            void this.runInWorker(job);
    }
    runInThread(job) {
        // A later task, not now: the caller's render finishes first, and an abort
        // before the task runs skips the work.
        job.timer = setTimeout(() => {
            job.timer = undefined;
            void this.runInThreadJob(job);
        }, 0);
    }
    async runInThreadJob(job) {
        let output;
        try {
            const jobs = await inThreadJobs();
            // An abort while the jobs loaded settled the job already.
            if (job.settled)
                return;
            output = jobs[job.name].run(job.input);
        }
        catch (error) {
            if (!job.settled)
                this.finish(job, () => job.reject(error));
            return;
        }
        this.finish(job, () => job.resolve(output));
    }
    async runInWorker(job) {
        let worker;
        try {
            worker = await this.acquireWorker();
        }
        catch (error) {
            // An aborted job's worker was stopped on purpose, which is no reason to fall back.
            if (job.settled)
                return;
            this.fallBack(error);
            this.runInThread(job);
            return;
        }
        if (job.settled)
            return;
        worker.postMessage({ id: job.id, job: job.name, input: job.input });
    }
    acquireWorker() {
        if (!this.worker) {
            const create = this.createWorker;
            if (!create)
                return Promise.reject(new Error('no worker factory'));
            const starting = Promise.resolve()
                .then(create)
                .then((worker) => {
                // Stopped while it started: nothing will post to it, so it must not outlive the stop.
                if (this.worker !== starting) {
                    worker.terminate();
                    throw abortError();
                }
                worker.addEventListener('message', ({ data }) => this.onReply(worker, data));
                worker.addEventListener('error', ({ message }) => this.onWorkerError(worker, message));
                this.liveWorker = worker;
                return worker;
            });
            this.worker = starting;
        }
        return this.worker;
    }
    onReply(worker, data) {
        const job = this.running;
        const reply = data;
        if (worker !== this.liveWorker || !job || job.id !== reply.id)
            return;
        if (reply.ok)
            this.finish(job, () => job.resolve(reply.output));
        else
            this.finish(job, () => job.reject(fromJobError(reply.error)));
    }
    /** A worker that fails to load raises `error` and never answers, so its job re-runs in-thread. */
    onWorkerError(worker, message) {
        if (worker !== this.liveWorker)
            return;
        this.stopWorker();
        this.fallBack(message ?? 'worker error');
        if (this.running)
            this.runInThread(this.running);
    }
    fallBack(reason) {
        if (this.inThread)
            return;
        this.inThread = true;
        warn(`[WorkerJobRunner] No job worker (${String(reason)}); running jobs on the main thread`);
    }
    abort(job) {
        if (job.settled)
            return;
        const queued = this.queue.indexOf(job);
        if (queued >= 0)
            this.queue.splice(queued, 1);
        if (job === this.running) {
            if (job.timer !== undefined)
                clearTimeout(job.timer);
            // Terminating is the only way to stop a job mid-run, and it frees its buffers.
            else
                this.stopWorker();
        }
        this.finish(job, () => job.reject(abortError()));
    }
    stopWorker() {
        this.liveWorker?.terminate();
        this.liveWorker = null;
        this.worker = null;
    }
    finish(job, settle) {
        job.settled = true;
        job.signal?.removeEventListener('abort', job.onAbort);
        if (this.running === job)
            this.running = null;
        settle();
        this.pump();
    }
}
/** A runner with no worker, for a caller outside any host: its jobs run on the main thread. */
export const inThreadJobRunner = new WorkerJobRunner();
//# sourceMappingURL=WorkerJobRunner.js.map